import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/lib/i18n";

function LegalLayout({ title, children, testid }) {
  const { t } = useLang();
  return (
    <div className="min-h-screen bg-[#1A1715] px-5 py-10">
      <div className="mx-auto max-w-2xl" data-testid={testid}>
        <div className="flex items-center justify-between pr-24">
          <Logo size="text-3xl" />
          <Link
            to="/gave"
            className="flex items-center gap-1.5 text-sm text-[#A8A29E] hover:text-[#D4AF37]"
            data-testid="legal-back"
          >
            <ArrowLeft className="h-4 w-4" /> {t("Tilbake", "Back")}
          </Link>
        </div>
        <h1 className="mt-10 font-serif-display text-4xl font-600 text-[#FDFBF7]">{title}</h1>
        <div className="mt-6 space-y-5 text-sm leading-relaxed text-[#A8A29E]">{children}</div>
        <p className="mt-10 text-xs text-[#A8A29E]/50">
          {t("Sist oppdatert", "Last updated")}: {new Date().getFullYear()}.{" "}
          {t(
            "Dette er et utkast til juridisk tekst og bør gjennomgås av Demo før publisering.",
            "This is a draft legal text and should be reviewed by Demo before publishing."
          )}
        </p>
      </div>
    </div>
  );
}

function Section({ heading, children }) {
  return (
    <section>
      <h2 className="mb-1.5 font-semibold text-[#FDFBF7]">{heading}</h2>
      <p>{children}</p>
    </section>
  );
}

const VILKAR = {
  no: [
    ["1. Om tilbudet", "Ved å registrere deg mottar du én personlig kode som gir rett til én gratis dessert ved besøk hos Demo. Tilbudet gjelder per gjest og kan ikke kombineres med andre tilbud."],
    ["2. Bruk av koden", "Koden er personlig, kan brukes én gang og må vises til servitøren ved besøk. Koden har ingen kontantverdi og kan ikke overdras eller løses inn i penger."],
    ["3. Én kode per gjest", "Hver e-postadresse gir kun én kode. Forsøk på å registrere seg flere ganger med samme e-postadresse gir samme, allerede utstedte kode."],
    ["4. Endringer", "Demo forbeholder seg retten til å endre eller avslutte kampanjen når som helst."],
  ],
  en: [
    ["1. About the offer", "By signing up you receive one personal code entitling you to one free dessert when visiting Demo. The offer applies per guest and cannot be combined with other offers."],
    ["2. Using the code", "The code is personal, can be used once and must be shown to the waiter when visiting. The code has no cash value and cannot be transferred or exchanged for money."],
    ["3. One code per guest", "Each email address gives only one code. Attempts to sign up several times with the same email address return the same, already issued code."],
    ["4. Changes", "Demo reserves the right to change or end the campaign at any time."],
  ],
};

const PERSONVERN = {
  no: [
    ["1. Behandlingsansvarlig", "Demo er behandlingsansvarlig for personopplysningene som samles inn gjennom registreringen."],
    ["2. Hvilke opplysninger vi lagrer", "Vi lagrer fornavn, e-postadresse, telefonnummer, tidspunkt for registrering, hvilke samtykker du har gitt, samt din unike kode og om den er brukt."],
    ["3. Formål", "Opplysningene brukes for å utstede og administrere din gratis dessert. Dersom du har samtykket til markedsføring, bruker vi i tillegg e-post og telefonnummer til å sende deg tilbud, nyheter og invitasjoner."],
    ["4. Markedsføringssamtykke", "Markedsføring sendes kun til gjester som aktivt har samtykket. Har du ikke samtykket, blir du ikke lagt til i noen markedsføringsliste. Du kan når som helst trekke tilbake samtykket ditt."],
    ["5. Dine rettigheter", "Du har rett til innsyn, retting og sletting av dine personopplysninger. Ta kontakt med Demo for å utøve dine rettigheter."],
  ],
  en: [
    ["1. Data controller", "Demo is the data controller for the personal data collected through the sign-up."],
    ["2. What data we store", "We store your first name, email address, phone number, time of sign-up, which consents you have given, and your unique code and whether it has been used."],
    ["3. Purpose", "The data is used to issue and manage your free dessert. If you have consented to marketing, we also use your email and phone number to send you offers, news and invitations."],
    ["4. Marketing consent", "Marketing is only sent to guests who have actively consented. If you have not consented, you will not be added to any marketing list. You can withdraw your consent at any time."],
    ["5. Your rights", "You have the right to access, correct and delete your personal data. Contact Demo to exercise your rights."],
  ],
};

function LegalPage({ titleNo, titleEn, sections, testid }) {
  const { lang, t } = useLang();
  return (
    <LegalLayout title={t(titleNo, titleEn)} testid={testid}>
      {sections[lang].map(([h, body]) => (
        <Section key={h} heading={h}>{body}</Section>
      ))}
    </LegalLayout>
  );
}

export function Vilkar() {
  return <LegalPage titleNo="Vilkår" titleEn="Terms" sections={VILKAR} testid="vilkar-page" />;
}

export function Personvern() {
  return <LegalPage titleNo="Personvernerklæring" titleEn="Privacy policy" sections={PERSONVERN} testid="personvern-page" />;
}

export default Vilkar;
